import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';
import { cookieConsentStateAtom } from '~/components/organism/CookieBanner/state';
import {
  initializeClarity,
  initializeGAAnalytics,
  safeClarity,
  safeGtag,
} from '~/components/organism/CookieBanner/utils/initializeAnalytics';
import type { GetSessionHydrationQuery } from '~/graphql/types';
import { isProduction } from '~/util';

type Args = {
  sessionHydration?: GetSessionHydrationQuery['getSessionHydration'];
};

const useSetupAnalytics = ({ sessionHydration }: Args) => {
  const consentState = useRecoilValue(cookieConsentStateAtom);

  // Update gtag and Clarity consent whenever consentState changes
  // This must run immediately when consent changes, regardless of user data availability
  useEffect(() => {
    // 1) Update GA consent
    safeGtag('consent', 'update', consentState);

    // 2) Update Clarity consent
    safeClarity('consentv2', {
      analytics_Storage: consentState.analytics_storage,
      ad_Storage: 'denied',
    });
  }, [consentState]);

  // Initialize analytics when consent is granted and user data is available
  useEffect(() => {
    // Allow tracking to dev analytics
    const isProd = isProduction();
    const devEmails = ['@dathuis.nl', '@bndl.nl'];
    if (!sessionHydration) return;
    if (sessionHydration.impersonatedBy != null) return;
    if (
      isProd &&
      devEmails.some(email => sessionHydration.me.email.endsWith(email))
    )
      return;

    const hasAnalyticsGranted = consentState.analytics_storage === 'granted';

    if (hasAnalyticsGranted) {
      void initializeGAAnalytics({ userId: sessionHydration.me.id });

      // Re-apply GA consent after GA is definitely available
      safeGtag('consent', 'update', consentState);

      initializeClarity({
        userId: sessionHydration.me.id,
        accountId: sessionHydration.account.id,
      });

      // Re-apply Clarity consent after Clarity init
      safeClarity('consentv2', {
        analytics_Storage: consentState.analytics_storage,
        ad_Storage: 'denied',
      });
    }
  }, [consentState, sessionHydration]);
};

export default useSetupAnalytics;
